import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { calculateTotals, formatCurrency, getStudentPrice } from './pricing';

const RECEIPT_GREEN = [21, 128, 61];
const RECEIPT_AMBER = [180, 83, 9];

function titleCase(value) {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}

/**
 * Normalizes payment metadata (from either the submit call or the API response)
 * into the fields the receipt renders.
 */
function resolveReceipt(payment = {}) {
  const method = payment.method === 'check' ? 'check' : 'card';
  const status = String(payment.status || '');
  const isDemo = Boolean(payment.demo) || status === 'demo_authorized';

  let label;
  let color;
  let paid;
  if (method === 'check') {
    label = 'PAYMENT PENDING — PAY BY CHECK';
    color = RECEIPT_AMBER;
    paid = false;
  } else if (isDemo) {
    label = 'TEST MODE — CARD NOT CHARGED';
    color = RECEIPT_AMBER;
    paid = false;
  } else if (status === 'succeeded' || status === 'requires_capture') {
    // requires_capture: authorized, and captured as soon as the registration is saved.
    label = 'PAID';
    color = RECEIPT_GREEN;
    paid = true;
  } else {
    label = `PAYMENT ${(status || 'pending').toUpperCase()}`;
    color = RECEIPT_AMBER;
    paid = false;
  }

  const card = payment.card && payment.card.last4 ? payment.card : null;
  const cardLine = card
    ? `${titleCase(card.brand) || 'Card'} ending in ${card.last4}${
        card.exp_month && card.exp_year
          ? ` (exp ${card.exp_month}/${String(card.exp_year).slice(-2)})`
          : ''
      }`
    : null;

  return {
    method,
    label,
    color,
    paid,
    cardLine,
    checkNumber: payment.checkNumber || null,
    reference: payment.reference || payment.paymentIntentId || payment.id || null,
  };
}

export function generateRegistrationPdf(form, options = {}) {
  const doc = new jsPDF();
  const totals = calculateTotals(
    form.registeringStudents === 'yes' ? form.students : [],
  );
  const submittedAt = options.submittedAt || new Date();

  doc.setFontSize(18);
  doc.text('IMC School Registration', 14, 18);
  doc.setFontSize(10);
  doc.setTextColor(90);
  doc.text(`Submitted: ${submittedAt.toLocaleString()}`, 14, 26);
  doc.text(`School Code: ${form.school.schoolCode.toUpperCase()}`, 14, 32);
  doc.setTextColor(0);

  doc.setFontSize(13);
  doc.text('School Information', 14, 44);
  autoTable(doc, {
    startY: 48,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 45 } },
    body: [
      ['School Name', form.school.name],
      ['ACSI ID #', form.school.acsiId],
      [
        'Address',
        [
          form.school.address1,
          form.school.address2,
          `${form.school.city}, ${form.school.state} ${form.school.zip}`,
          form.school.country,
        ]
          .filter(Boolean)
          .join('\n'),
      ],
      ['School Code', form.school.schoolCode.toUpperCase()],
      ['Competition Level', form.competitionLevel || '—'],
    ],
  });

  let y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(13);
  doc.text('School Contacts / Advisors', 14, y);
  autoTable(doc, {
    startY: y + 4,
    head: [['Name', 'Title', 'Phone', 'Email', 'Primary']],
    body: form.contacts.map((contact, index) => [
      `${contact.firstName} ${contact.lastName}`,
      contact.title || '—',
      contact.phone,
      contact.email,
      index === 0 ? 'Yes' : 'No',
    ]),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [35, 100, 170] },
  });

  y = doc.lastAutoTable.finalY + 10;

  if (form.registeringStudents === 'yes' && form.students.length) {
    doc.setFontSize(13);
    doc.text('Student Registration', 14, y);
    autoTable(doc, {
      startY: y + 4,
      head: [['Student Name', 'Grade Level', 'Tests', 'Price']],
      body: form.students.map((student) => [
        `${student.firstName} ${student.lastName}`,
        student.gradeLevel,
        [
          student.tests.reasoning ? 'Reasoning' : null,
          student.tests.computation ? 'Computation' : null,
        ]
          .filter(Boolean)
          .join(', '),
        formatCurrency(getStudentPrice(student)),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [35, 100, 170] },
    });
    y = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFontSize(11);
    doc.text('Students: Not registering at this time.', 14, y);
    y += 10;
  }

  const receipt = resolveReceipt(options.payment);

  doc.setFontSize(13);
  doc.text('Payment Receipt', 14, y);
  doc.setFontSize(12);
  doc.setTextColor(...receipt.color);
  doc.text(receipt.label, 14, y + 8);
  doc.setTextColor(0);

  const receiptRows = [
    ['Student Fees', formatCurrency(totals.studentFees)],
    [receipt.paid ? 'Amount Paid' : 'Amount Due', formatCurrency(totals.total)],
    ['Payment Method', receipt.method === 'check' ? 'Check' : 'Credit Card'],
  ];
  if (receipt.cardLine) {
    receiptRows.push(['Card', receipt.cardLine]);
  }
  if (receipt.method === 'check' && receipt.checkNumber) {
    receiptRows.push(['Check Number', receipt.checkNumber]);
  }
  receiptRows.push([receipt.paid ? 'Payment Date' : 'Date', submittedAt.toLocaleString()]);
  if (receipt.reference && receipt.method !== 'check') {
    receiptRows.push(['Reference', receipt.reference]);
  }

  autoTable(doc, {
    startY: y + 12,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    body: receiptRows,
  });

  const filename = `IMC_Registration_${form.school.schoolCode || 'School'}.pdf`;
  return { doc, filename, blob: doc.output('blob') };
}

export function downloadRegistrationPdf(form, options = {}) {
  const { doc, filename } = generateRegistrationPdf(form, options);
  doc.save(filename);
  return filename;
}
